import { useState, useRef, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import HTMLFlipBook from 'react-pageflip';
import type { Photo } from '@/types';
import { Modal } from '@/components/ui/Modal';
import LikeButton from './LikeButton';
import CommentList from './CommentList';
import { recordView } from '@/hooks/useGallery';

interface MagazineGalleryProps {
  photos: Photo[];
  mockImageFn?: (photo: Photo) => string;
  eventName?: string;
  eventDate?: string;
  /**
   * When true, the magazine assumes its parent already controls the
   * viewport height (e.g. GalleryPage with a sticky header). In that
   * case it sizes itself to fill the parent instead of using 100dvh.
   */
  fillParent?: boolean;
}

function getImageUrl(photo: Photo, mockFn?: (p: Photo) => string): string {
  // Hero / cover local: intotdeauna prioritar
  if (photo.static_url) return photo.static_url;
  if (mockFn) {
    return mockFn(photo);
  }
  return `/api/telegram/file/${encodeURIComponent(photo.telegram_file_id ?? '')}`;
}

/**
 * Compute the book container's pixel size and single-page dimensions.
 *
 * On mobile (vw < 768): Dual vertical spread (Top Page + Bottom Page).
 * The book is rendered as a horizontal 2-page spread internally (bookW x bookH)
 * and rotated 90° via CSS so it displays vertically as:
 * - Top Page (Left page in StPageFlip)
 * - Bottom Page (Right page in StPageFlip)
 * with a horizontal spine between them. Gestures are transformed inversely
 * so pulling UP flips from bottom to top, and pulling DOWN flips top to bottom!
 *
 * On desktop (vw >= 768): Dual horizontal spread (Left Page + Right Page).
 *
 * The `availableH` argument tells the calculator how much vertical space the
 * book is allowed to occupy (in CSS pixels). The book is then sized to fit
 * that budget exactly so the page never needs to scroll.
 */
function computeBookSize(availableH?: number) {
  if (typeof window === 'undefined') {
    return {
      pageW: 400,
      pageH: 560,
      bookW: 800,
      bookH: 560,
      screenWidth: 800,
      screenHeight: 560,
      isPhone: false,
    };
  }

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const isPhone = vw < 768;

  if (isPhone) {
    // Dual page spread on phone: Top page + Bottom page.
    // On a phone the book is rotated 90° so its on-screen size is:
    //   width  = bookH (the spread's height in book coords)
    //   height = bookW = 2 * pageW
    // `availableH` is the on-screen vertical budget for the book only.
    const screenW = Math.min(vw - 16, 430);
    const bookH = screenW;
    // On screen the book occupies bookW (vertical) of the available height.
    const maxBookW = Math.max(120, availableH ?? vh - 100);
    let pageW = Math.floor(maxBookW / 2);
    pageW = Math.min(pageW, 360);
    const bookW = pageW * 2;

    return {
      pageW,
      pageH: bookH,
      bookW,
      bookH,
      screenWidth: screenW,
      screenHeight: bookW,
      isPhone: true,
    };
  }

  // Desktop 2-page spread: Left + Right.
  const maxSpreadW = Math.min(vw - 100, 920);
  const safeH = Math.max(180, availableH ?? vh - 120);
  const maxSpreadH = safeH;

  let pageW = Math.floor(maxSpreadW / 2);
  let pageH = Math.round(pageW * 1.38);

  if (pageH > maxSpreadH) {
    pageH = maxSpreadH;
    pageW = Math.round(pageH / 1.38);
  }

  const bookW = pageW * 2;
  const bookH = pageH;

  return {
    pageW,
    pageH,
    bookW,
    bookH,
    screenWidth: bookW,
    screenHeight: bookH,
    isPhone: false,
  };
}

export default function MagazineGallery({ photos, mockImageFn, eventName, eventDate, fillParent }: MagazineGalleryProps) {
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const flipBook = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isPointerDownRef = useRef(false);
  const pointerStartPos = useRef<{ x: number; y: number } | null>(null);
  const hasDraggedRef = useRef(false);

  // Cheie stabilă care forțează remount curat la schimbări de date (evită insertBefore error în react-pageflip)
  const photosKey = useMemo(() => {
    return photos.map((p) => `${p.id}_${p.uploader_name}_${p.likes_count}_${p.comments_count}`).join('|');
  }, [photos]);

  const [bookSize, setBookSize] = useState(() => computeBookSize());
  const wrapRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number | undefined>(undefined);

  // Once the user actually interacts with the book (flip or arrow press)
  // we hide the corner-lift hint forever.
  const [hintHidden, setHintHidden] = useState(false);
  const dismissHint = useCallback(() => setHintHidden(true), []);

  const handleFlipPage = useCallback((pageIndex: number) => {
    setCurrentPage(pageIndex);
    dismissHint();
  }, [dismissHint]);

  // Prefetch 10 poze în avans în cache-ul HTTP al browserului (fără modificări DOM în react-pageflip)
  useEffect(() => {
    const start = Math.max(0, currentPage);
    const end = Math.min(photos.length, currentPage + 10);
    for (let i = start; i < end; i++) {
      const p = photos[i];
      if (p) {
        const url = getImageUrl(p, mockImageFn);
        if (url) {
          const img = new Image();
          img.src = url;
        }
      }
    }
  }, [currentPage, photos, mockImageFn]);

  useEffect(() => {
    const onResize = () => {
      // If we have measured the actual viewport-area for the book (excluding
      // sticky bars), use that. Fall back to window inner height on first paint.
      setBookSize(computeBookSize(containerHeight));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [containerHeight]);

  // Measure the real available height for the book once the layout exists.
  // We measure the wrapper element minus the top + bottom bars inside it.
  // If the wrapper's top is below the page's own header, we also subtract
  // that so we never overflow the viewport.
  useEffect(() => {
    const measure = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const top = wrap.querySelector('[data-mag-bar="top"]') as HTMLElement | null;
      const bot = wrap.querySelector('[data-mag-bar="bot"]') as HTMLElement | null;
      const topH = top?.offsetHeight ?? 40;
      const botH = bot?.offsetHeight ?? 44;
      const innerAvailable = wrap.clientHeight - topH - botH;
      // If our wrap is taller than the viewport's distance from our top to
      // the bottom of the window, shrink it so we never cause page scroll.
      const rect = wrap.getBoundingClientRect();
      const viewport = window.innerHeight;
      const overflow = rect.height - (viewport - rect.top);
      const safeAvailable = innerAvailable - Math.max(0, overflow);
      if (safeAvailable > 100) setContainerHeight(safeAvailable);
    };
    measure();
    window.addEventListener('resize', measure);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure);
    }
    // re-measure after a short delay (covers late layout shifts from images)
    const t = setTimeout(measure, 250);
    return () => {
      window.removeEventListener('resize', measure);
      clearTimeout(t);
    };
  }, []);

  // Custom pointer event handling with inverse rotation mapping for mobile dual spread!
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const getPageFlipPos = (e: PointerEvent) => {
      const pageFlip = flipBook.current?.pageFlip?.();
      if (!pageFlip) return null;
      const distEl = pageFlip.getUI()?.getDistElement?.();
      if (!distEl) return null;
      const rect = distEl.getBoundingClientRect();

      if (bookSize.isPhone) {
        // Center of the rotated book on screen
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const u = e.clientX - cx;
        const v = e.clientY - cy;

        // Inverse mapping of +90deg rotation:
        // u = -y_bc => y_bc = -u
        // v =  x_bc => x_bc =  v
        const xBook = v + bookSize.bookW / 2;
        const yBook = -u + bookSize.bookH / 2;

        return { x: xBook, y: yBook };
      }

      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };

    const handlePointerDown = (e: PointerEvent) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      if ((e.target as HTMLElement).closest('button, a, .modal')) return;

      dismissHint();

      const pageFlip = flipBook.current?.pageFlip?.();
      if (!pageFlip) return;

      const pos = getPageFlipPos(e);
      if (!pos) return;

      isPointerDownRef.current = true;
      pointerStartPos.current = { x: e.clientX, y: e.clientY };
      hasDraggedRef.current = false;

      pageFlip.startUserTouch(pos);

      try {
        container.setPointerCapture(e.pointerId);
      } catch (_) {}
    };

    const handlePointerMove = (e: PointerEvent) => {
      const pageFlip = flipBook.current?.pageFlip?.();
      if (!pageFlip) return;

      const pos = getPageFlipPos(e);
      if (!pos) return;

      if (isPointerDownRef.current) {
        if (pointerStartPos.current) {
          const dx = e.clientX - pointerStartPos.current.x;
          const dy = e.clientY - pointerStartPos.current.y;
          if (Math.hypot(dx, dy) > 4) {
            hasDraggedRef.current = true;
          }
        }
        pageFlip.userMove(pos, false);
        if (e.cancelable) {
          e.preventDefault();
        }
      } else if (e.pointerType === 'mouse') {
        pageFlip.userMove(pos, false);
      }
    };

    const handlePointerUp = (e: PointerEvent) => {
      if (!isPointerDownRef.current) return;
      isPointerDownRef.current = false;

      const pageFlip = flipBook.current?.pageFlip?.();
      if (!pageFlip) return;

      const pos = getPageFlipPos(e) || { x: 0, y: 0 };
      pageFlip.userStop(pos, false);

      try {
        container.releasePointerCapture(e.pointerId);
      } catch (_) {}
    };

    const handlePointerCancel = (e: PointerEvent) => {
      if (!isPointerDownRef.current) return;
      isPointerDownRef.current = false;

      const pageFlip = flipBook.current?.pageFlip?.();
      if (!pageFlip) return;

      const pos = getPageFlipPos(e) || { x: 0, y: 0 };
      pageFlip.userStop(pos, false);
    };

    container.addEventListener('pointerdown', handlePointerDown);
    container.addEventListener('pointermove', handlePointerMove);
    container.addEventListener('pointerup', handlePointerUp);
    container.addEventListener('pointercancel', handlePointerCancel);

    return () => {
      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('pointermove', handlePointerMove);
      container.removeEventListener('pointerup', handlePointerUp);
      container.removeEventListener('pointercancel', handlePointerCancel);
    };
  }, [bookSize, dismissHint]);

  const minPageW = 100;
  const maxPageW = 800;
  const minPageH = 100;
  const maxPageH = 800;

  const needsBlank = photos.length % 2 === 1;
    const totalPages = photos.length + (needsBlank ? 1 : 0) + 1;

  const handlePhotoClick = (photo: Photo) => {
    if (!hasDraggedRef.current) {
      setSelectedPhoto(photo);
      recordView(photo.id);
    }
  };

  // Helper to render content inside page, supporting counter-rotation when on mobile
  const renderPageInner = (content: ReactNode, onClick?: () => void) => {
    if (bookSize.isPhone) {
      return (
        <div
          onClick={onClick}
          className="page-inner-phone"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: `${bookSize.bookH}px`, // screen width
            height: `${bookSize.pageW}px`, // screen page height
            transform: 'translate(-50%, -50%) rotate(-90deg)',
            transformOrigin: 'center center',
            overflow: 'hidden',
          }}
        >
          {content}
        </div>
      );
    }

    return (
      <div onClick={onClick} className="h-full w-full relative overflow-hidden">
        {content}
      </div>
    );
  };


  const photoPages: ReactNode[] = photos.map((photo) => {
      const isHero = photo.is_hero === true;
      return (
        <div
          key={photo.id}
          className="book-page group"
          style={{
            background: 'var(--color-bg-alt)',
            width: '100%',
            height: '100%',
            position: 'relative',
            cursor: isHero ? 'default' : 'pointer',
          }}
        >
          {renderPageInner(
            <div className="h-full w-full relative overflow-hidden bg-bg-alt p-2">
              <img
                src={getImageUrl(photo, mockImageFn)}
                alt={photo.caption || `Photo by ${photo.uploader_name}`}
                loading={isHero ? 'eager' : 'lazy'}
                decoding="async"
                className="h-full w-full object-cover select-none pointer-events-none rounded-sm"
                draggable={false}
              />

              {/* Overlay text pentru hero (prima pagina foto) */}
              {isHero && (
                <>
                  <div
                    className="absolute inset-0 pointer-events-none rounded-sm"
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.10) 35%, rgba(0,0,0,0.10) 65%, rgba(0,0,0,0.55) 100%)',
                    }}
                    aria-hidden
                  />
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none px-4">
                    <p
                      className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.32em] text-white/85 drop-shadow"
                      style={{ textShadow: '0 2px 12px rgba(0,0,0,0.6)' }}
                    >
                      Album de nuntă
                    </p>
                    <h2
                      className="mt-3 font-serif text-white drop-shadow-lg"
                      style={{
                        fontSize: 'clamp(1.6rem, 5.5vw, 3rem)',
                        fontVariationSettings: "'opsz' 144, 'SOFT' 100, 'WONK' 1",
                        textShadow: '0 4px 24px rgba(0,0,0,0.7)',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {eventName || 'Larisa & Dani'}
                    </h2>
                    {eventDate && (
                      <p
                        className="mt-3 font-mono text-[10px] sm:text-xs text-white/80 tracking-widest uppercase drop-shadow"
                        style={{ textShadow: '0 2px 12px rgba(0,0,0,0.7)' }}
                      >
                        {new Date(eventDate).toLocaleDateString('ro-RO', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </p>
                    )}
                    <div className="mt-5 flex items-center gap-3 max-w-[200px] w-full">
                      <span className="flex-1 h-px bg-white/40" />
                      <span className="text-white/70 text-sm" aria-hidden>❧</span>
                      <span className="flex-1 h-px bg-white/40" />
                    </div>
                  </div>
                </>
              )}

              {/* Bottom overlay normal (doar pentru non-hero) */}
              {!isHero && (
                <div
                  className="absolute bottom-0 left-0 right-0 p-2.5 sm:p-3.5 pointer-events-none"
                  style={{
                    background: 'linear-gradient(to top, rgba(0,0,0,0.72), rgba(0,0,0,0.15) 65%, transparent)',
                    color: 'white',
                  }}
                >
                  <p className="font-serif text-xs sm:text-base italic drop-shadow-sm">
                    {photo.uploader_name}
                  </p>
                  <p className="font-mono text-[10px] sm:text-xs text-white/80 drop-shadow-sm">
                    {new Date(photo.uploaded_at).toLocaleDateString('ro-RO', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </p>
                </div>
              )}
            </div>,
            isHero ? undefined : () => handlePhotoClick(photo)
          )}
        </div>
      );
    });

  const blankPage: ReactNode = needsBlank ? (
    <div
      key="__blank__"
      className="book-page"
      style={{
        background: 'var(--color-bg-alt)',
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    >
      {renderPageInner(
        <div className="h-full w-full flex items-center justify-center select-none">
          <p className="font-serif italic text-sm text-fg/50">·</p>
        </div>
      )}
    </div>
  ) : null;

  const backCover: ReactNode = (
    <div
      key="__back__"
      className="book-page"
      style={{
        background: 'var(--color-bg-alt)',
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    >
      {renderPageInner(
        <div className="h-full w-full flex flex-col items-center justify-center text-center p-6 select-none">
          <p className="font-serif italic text-sm sm:text-base text-fg/70">
            GuestLens
          </p>
          <p className="font-mono text-[10px] sm:text-xs text-fg/40 mt-1">
            Ochii invitaților într-o singură galerie
          </p>
        </div>
      )}
    </div>
  );

  const handlePrev = () => {
    dismissHint();
    if (flipBook.current && typeof flipBook.current.pageFlip === 'function') {
      flipBook.current.pageFlip().flipPrev();
    }
  };

  const handleNext = () => {
    dismissHint();
    if (flipBook.current && typeof flipBook.current.pageFlip === 'function') {
      flipBook.current.pageFlip().flipNext();
    }
  };

  return (
    <>
      {/* If fillParent, the parent already gave us a sized container (with
          a sticky header on top). Otherwise we lock to the viewport ourselves
          and provide our own top + bottom bars. */}
      {fillParent ? (
        <div
          className={`flex h-full w-full flex-col ${hintHidden ? 'magazine-hint-hidden' : ''}`}
        >
          <BookArea
            photosKey={photosKey}
            bookSize={bookSize}
            containerRef={containerRef}
            flipBook={flipBook}
            photos={photos}
            needsBlank={needsBlank}
            photoPages={photoPages}
            blankPage={blankPage}
            backCover={backCover}
            minPageW={minPageW}
            maxPageW={maxPageW}
            minPageH={minPageH}
            maxPageH={maxPageH}
            handlePrev={handlePrev}
            handleNext={handleNext}
            hintHidden={hintHidden}
            onFlip={(e: any) => handleFlipPage(e.data)}
          />
        </div>
      ) : (
        <div
          ref={wrapRef}
          className={`flex flex-col bg-bg select-none ${hintHidden ? 'magazine-hint-hidden' : ''}`}
          style={{ height: '100dvh', maxHeight: '100dvh' }}
        >
          <StickyTopBar eventName={eventName} bookSize={bookSize} />
          <BookArea
            photosKey={photosKey}
            bookSize={bookSize}
            containerRef={containerRef}
            flipBook={flipBook}
            photos={photos}
            needsBlank={needsBlank}
            photoPages={photoPages}
            blankPage={blankPage}
            backCover={backCover}
            minPageW={minPageW}
            maxPageW={maxPageW}
            minPageH={minPageH}
            maxPageH={maxPageH}
            handlePrev={handlePrev}
            handleNext={handleNext}
            hintHidden={hintHidden}
            onFlip={(e: any) => handleFlipPage(e.data)}
          />
          <StickyBottomCounter
            currentPage={currentPage}
            totalPages={totalPages}
          />
        </div>
      )}

      <Modal open={!!selectedPhoto} onClose={() => setSelectedPhoto(null)}>
        {selectedPhoto && (
          <div className="max-h-[85vh] overflow-y-auto">
            <img
              src={getImageUrl(selectedPhoto, mockImageFn)}
              alt={selectedPhoto.caption || ''}
              className="max-h-[55vh] w-full rounded-md object-contain bg-bg"
            />
            <div className="mt-md flex items-center justify-between">
              <div>
                <p className="font-serif text-lg leading-tight">{selectedPhoto.uploader_name}</p>
                {selectedPhoto.caption && (
                  <p className="mt-1 text-sm text-fg-soft">{selectedPhoto.caption}</p>
                )}
                <p className="font-mono text-xs text-fg/70 mt-1">
                  {new Date(selectedPhoto.uploaded_at).toLocaleDateString('ro-RO')}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-mono text-fg-soft">
                  👁 {selectedPhoto.views_count ?? 0}
                </span>
                <LikeButton photoId={selectedPhoto.id} initialCount={selectedPhoto.likes_count} />
              </div>
            </div>

            {/* Comment Section */}
            <CommentList photoId={selectedPhoto.id} />
          </div>
        )}
      </Modal>

      <style>{`
        .magazine-container {
          display: flex;
          align-items: center;
          justify-content: center;
          user-select: none;
          -webkit-user-select: none;
          position: relative;
        }
        .magazine-container.rotated-spread .stf__parent {
          transform: rotate(90deg);
          transform-origin: center center;
        }
        .magazine-book {
          margin: 0 auto;
          box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.25);
          border-radius: 4px;
        }
        .book-page {
          background: var(--color-bg-alt);
          -webkit-backface-visibility: visible;
          backface-visibility: visible;
          transform-style: preserve-3d;
          will-change: transform;
          user-select: none;
          -webkit-user-select: none;
          box-shadow: inset 0 0 30px rgba(0, 0, 0, 0.05);
        }
        .book-page img {
          user-select: none;
          -webkit-user-drag: none;
          -webkit-user-select: none;
        }
        .stf__block {
          background: var(--color-bg-alt);
        }
        .stf__parent {
          touch-action: none !important;
        }

        /* ─── Idle corner lift hint ───
           A small dark wedge at the bottom-right corner of the right page
           that subtly lifts and falls to suggest "you can grab this".
           Auto-stops the moment the user actually interacts with the book. */
        @keyframes magazine-corner-lift {
          0%   { transform: rotate(0deg); }
          45%  { transform: rotate(-22deg); }
          70%  { transform: rotate(-22deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes magazine-corner-shadow {
          0%   { opacity: 0.05; transform: scale(0.7); }
          45%  { opacity: 0.22; transform: scale(1); }
          70%  { opacity: 0.22; transform: scale(1); }
          100% { opacity: 0.05; transform: scale(0.7); }
        }
        .magazine-corner-hint {
          position: absolute;
          right: 0;
          bottom: 0;
          width: 38px;
          height: 38px;
          background: linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.18) 50%);
          pointer-events: none;
          z-index: 4;
          transform-origin: 100% 100%;
          animation: magazine-corner-lift 3.4s var(--ease-out, cubic-bezier(0.16, 1, 0.3, 1)) infinite;
          border-bottom-right-radius: 4px;
        }
        .magazine-corner-hint::before {
          content: '';
          position: absolute;
          right: 4px;
          bottom: 4px;
          width: 30px;
          height: 6px;
          border-radius: 999px;
          background: radial-gradient(ellipse at center, rgba(0,0,0,0.22), transparent 70%);
          filter: blur(2px);
          animation: magazine-corner-shadow 3.4s var(--ease-out, cubic-bezier(0.16, 1, 0.3, 1)) infinite;
        }
        /* Sits inside the right page so it rotates with the book on phone */
        .stf__item:nth-child(2n) .magazine-corner-hint,
        .stf__block .magazine-corner-hint {
          display: block;
        }
        /* On the rotated (phone) spread the hint should hide on the LEFT
           page (it's behind the spine). We only show it on the RIGHT page
           by attaching it to even children in book order. If you see it on
           the wrong page, the simplest fix is to attach it dynamically via
           a state flag. */
        .magazine-hint-hidden .magazine-corner-hint,
        .magazine-hint-hidden .magazine-corner-hint::before {
          animation: none;
          opacity: 0;
        }
      `}</style>
    </>
  );
}

/* ─── Internal helpers ─── */

function StickyTopBar({
  eventName,
  bookSize,
}: {
  eventName?: string;
  bookSize: { isPhone: boolean };
}) {
  return (
    <div
      data-mag-bar="top"
      className="z-20 shrink-0 border-b border-border/50 bg-bg px-md py-2"
    >
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-fg/60 sm:text-xs">
          {bookSize.isPhone ? 'Trage de colț ↕' : 'Trage de colț sau săgeți'}
        </p>
        <p className="font-serif text-xs italic text-fg/70 sm:text-sm">
          {eventName || 'Galeria'}
        </p>
      </div>
    </div>
  );
}

function StickyBottomCounter({
  currentPage,
  totalPages,
}: {
  currentPage: number;
  totalPages: number;
}) {
  return (
    <div
      data-mag-bar="bot"
      className="z-20 shrink-0 border-t border-border/50 bg-bg/85 px-md py-2 backdrop-blur"
    >
      <div className="flex items-center justify-center gap-md">
        <span className="inline-flex items-center gap-2 rounded-full bg-bg-alt/80 px-3 py-1 font-mono text-xs text-fg/80 border border-border/60">
          <span className="font-semibold text-fg">{currentPage + 1}</span>
          <span className="text-fg/50">/</span>
          <span>{totalPages}</span>
        </span>
      </div>
    </div>
  );
}

function BookArea({
  photosKey,
  bookSize,
  containerRef,
  flipBook,
  photos: _photos,
  needsBlank,
  photoPages,
  blankPage,
  backCover,
  minPageW,
  maxPageW,
  minPageH,
  maxPageH,
  handlePrev,
  handleNext,
  hintHidden,
  onFlip,
}: {
  photosKey: string;
  bookSize: ReturnType<typeof computeBookSize>;
  containerRef: React.RefObject<HTMLDivElement>;
  flipBook: React.RefObject<any>;
  photos: Photo[];
  needsBlank: boolean;
  photoPages: ReactNode[];
  blankPage: ReactNode;
  backCover: ReactNode;
  minPageW: number;
  maxPageW: number;
  minPageH: number;
  maxPageH: number;
  handlePrev: () => void;
  handleNext: () => void;
  hintHidden: boolean;
  onFlip: (e: any) => void;
}) {
  return (
    <div className="relative flex flex-1 items-center justify-center overflow-hidden px-1 sm:px-6">
      <div
        ref={containerRef}
        className={`magazine-container relative touch-none ${bookSize.isPhone ? 'rotated-spread' : ''}`}
        style={{
          width: `${bookSize.screenWidth}px`,
          height: `${bookSize.screenHeight}px`,
          touchAction: 'none',
          maxWidth: '100%',
          maxHeight: '100%',
        }}
      >
        <HTMLFlipBook
          key={photosKey}
          ref={flipBook}
          width={bookSize.pageW}
          height={bookSize.pageH}
          size="fixed"
          minWidth={minPageW}
          maxWidth={maxPageW}
          minHeight={minPageH}
          maxHeight={maxPageH}
          drawShadow={true}
          flippingTime={900}
          usePortrait={false}
          startZIndex={0}
          autoSize={false}
          maxShadowOpacity={0.7}
          showCover={false}
          mobileScrollSupport={false}
          clickEventForward={false}
          useMouseEvents={false}
          swipeDistance={15}
          showPageCorners={true}
          disableFlipByClick={true}
          startPage={0}
          style={{}}
          className="magazine-book"
          onFlip={onFlip}
        >
          {...(needsBlank
            ? [...photoPages, blankPage, backCover]
            : [...photoPages, backCover]
          ).filter((c): c is ReactNode => Boolean(c))}
        </HTMLFlipBook>
      </div>

      <button
        onClick={handlePrev}
        className="absolute left-1 top-1/2 z-10 -translate-y-1/2 rounded-full border border-border bg-bg-alt/85 p-2 shadow-md backdrop-blur transition hover:bg-bg-alt active:scale-95 sm:left-2 sm:p-2.5"
        aria-label="Pagina anterioară"
      >
        <span className="text-sm font-bold sm:text-base">{bookSize.isPhone ? '↑' : '←'}</span>
      </button>

      <button
        onClick={handleNext}
        className="absolute right-1 top-1/2 z-10 -translate-y-1/2 rounded-full border border-border bg-bg-alt/85 p-2 shadow-md backdrop-blur transition hover:bg-bg-alt active:scale-95 sm:right-2 sm:p-2.5"
        aria-label="Pagina următoare"
      >
        <span className="text-sm font-bold sm:text-base">{bookSize.isPhone ? '↓' : '→'}</span>
      </button>

      {/* Idle corner-lift hint: a tiny fold that lifts and falls to suggest
          "drag me". Auto-hides via the .magazine-hint-hidden class on the
          parent wrapper once the user has interacted. */}
      {!hintHidden && <span className="magazine-corner-hint" aria-hidden />}
    </div>
  );
}